// ────────────────────────────────────────────────────────────────────
// LeftTree — structural navigation panel (workbench roadmap A1). v1
// content: Plants -> Assets, from the same apiClient.plants()/assets()
// queries the rest of the app already uses (Atlas, AssetWorkspaceScreen) —
// no new endpoints. Collapsible, remembering both the collapsed-width state
// and which plants are expanded in localStorage.
//
// "แพ็กเกจเอกสาร" (document packages, roadmap B1) now navigates to
// /documents (DocumentsScreen) — the real package tree + revision control
// built in Stage B. It was an inert Stage-A placeholder node before this.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ChevronDown, Building2, Box, PanelLeft, PanelLeftClose, Archive, X,
         FileText, Network, ClipboardList, Radar, ShieldAlert, Activity } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { apiClient, getActiveTenant, getCachedUser, STAFF_ROLES } from "@/lib/api";
import type { Plant, Asset } from "@/lib/types";

// Per-asset facets (roadmap: the asset "binder"). Two kinds, kept in order:
// documents (DWG / P&ID / ITP) then assessments (NDT / RBI / FFS). Each node
// opens the asset workspace carrying ?facet=<key>; the workspace decides what
// to show per facet as each one gets a real view (NDT has data today; the rest
// currently land on the asset with the facet noted — honest, not a dead node).
const ASSET_FACETS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: "dwg", label: "DWG",  icon: FileText },
  { key: "pid", label: "P&ID", icon: Network },
  { key: "itp", label: "ITP",  icon: ClipboardList },
  { key: "ndt", label: "NDT",  icon: Radar },
  { key: "rbi", label: "RBI",  icon: ShieldAlert },
  { key: "ffs", label: "FFS",  icon: Activity },
];

const COLLAPSED_KEY = "xzynexa.tree.collapsed";
const EXPANDED_KEY = "xzynexa.tree.expandedPlants";

function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}
function writeCollapsed(v: boolean): void {
  try { localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0"); } catch { /* storage unavailable */ }
}
function readExpanded(): number[] {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === "number") : [];
  } catch { return []; }
}
function writeExpanded(ids: number[]): void {
  try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(ids)); } catch { /* storage unavailable */ }
}

// One asset row: a chevron toggles its facets (DWG/P&ID/ITP/NDT/RBI/FFS); the
// tag label still navigates to the asset workspace (unchanged contract).
function AssetNode({ asset, onNavigate }: { asset: Asset; onNavigate: () => void }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="w-full flex items-center hover:bg-[rgba(var(--accent-rgb)_/_0.08)]">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                aria-label={open ? `ย่อ ${asset.tag}` : `ขยาย ${asset.tag}`}
                data-testid={`tree-asset-toggle-${asset.id}`}
                className="pl-2 pr-0.5 py-1 shrink-0" style={{ color: "var(--ink-3)" }}>
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button type="button" data-testid={`tree-asset-${asset.id}`}
                onClick={() => { navigate(`/assets/${asset.id}`); onNavigate(); }}
                className="flex-1 flex items-center gap-1.5 pr-2 py-1 text-left font-mono min-w-0"
                style={{ fontSize: 11, color: "var(--ink-2)" }}>
          <Box size={11} style={{ color: "var(--ink-4)" }} />
          <span className="truncate">{asset.tag}</span>
        </button>
      </div>
      {open && (
        <div className="pl-[26px]">
          {ASSET_FACETS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.key} type="button"
                      data-testid={`tree-facet-${asset.id}-${f.key}`}
                      onClick={() => { navigate(`/assets/${asset.id}?facet=${f.key}`); onNavigate(); }}
                      className="w-full flex items-center gap-1.5 px-2 py-1 text-left font-mono hover:bg-[rgba(var(--accent-rgb)_/_0.08)]"
                      style={{ fontSize: 10.5, letterSpacing: ".04em", color: "var(--ink-3)" }}>
                <Icon size={10} style={{ color: "var(--ink-4)" }} />
                <span>{f.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlantNode({ plant, expanded, onToggle, onNavigate }: {
  plant: Plant; expanded: boolean; onToggle: () => void; onNavigate: () => void;
}): JSX.Element {
  const assetsQ = useQuery({
    queryKey: ["assets", { plant_id: plant.id }],
    queryFn: () => apiClient.assets({ plant_id: plant.id }),
    enabled: expanded, staleTime: 60_000,
  });
  const assets = assetsQ.data ?? [];
  return (
    <div>
      <button type="button" onClick={onToggle} data-testid={`tree-plant-${plant.id}`}
              aria-expanded={expanded}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left font-mono hover:bg-[rgba(31,78,121,0.05)]"
              style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Building2 size={12} style={{ color: "var(--ink-4)" }} />
        <span className="truncate">{plant.name}</span>
      </button>
      {expanded && (
        <div className="pl-6">
          {assetsQ.isPending && (
            <div className="px-2 py-1 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>กำลังโหลด…</div>
          )}
          {!assetsQ.isPending && assets.length === 0 && (
            <div className="px-2 py-1 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>ไม่มี asset</div>
          )}
          {assets.map((a) => (
            <AssetNode key={a.id} asset={a} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

export function LeftTree({ narrow, onCloseMobile }: { narrow?: boolean; onCloseMobile?: () => void }): JSX.Element {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [expandedIds, setExpandedIds] = useState<number[]>(readExpanded);
  useEffect(() => writeCollapsed(collapsed), [collapsed]);
  useEffect(() => writeExpanded(expandedIds), [expandedIds]);
  useEffect(() => {
    if (!narrow) return;
    function onKeyDown(e: KeyboardEvent) { if (e.key === "Escape") onCloseMobile?.(); }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [narrow, onCloseMobile]);

  // Tenant isolation is Postgres RLS, which fails closed: a request with no
  // tenant bound returns zero rows. "Zero rows" and "nothing exists" are
  // therefore byte-identical at the database and the UI has to draw that line
  // itself — this panel used to answer "ไม่มีไซต์" (no sites) when the truth was
  // that no customer had been picked yet, which is a lie about the customer's
  // data. Same rule as the file room’s import console: X-Tenant-Id comes from getActiveTenant(),
  // so only STAFF have this gap; a portal user has none, because
  // resolve_tenant_for_user falls back to their first membership.
  const user = getCachedUser();
  const isStaff = !!user && (STAFF_ROLES as readonly string[]).includes(user.role);
  const noTenant = isStaff && !getActiveTenant();

  const plantsQ = useQuery({
    queryKey: ["plants", { active_only: true }, getActiveTenant()],
    queryFn: () => apiClient.plants({ active_only: true }),
    enabled: !noTenant,      // it could only come back empty
    staleTime: 60_000,
  });
  const plants = plantsQ.data ?? [];

  function toggle(id: number): void {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  if (collapsed && !narrow) {
    return (
      <div className="shrink-0 border-r flex flex-col items-center py-2" style={{ width: 36, borderColor: "var(--hairline)" }}>
        <button type="button" onClick={() => setCollapsed(false)} aria-label="ขยายแผนผังไซต์" data-testid="tree-expand-toggle"
                className="p-1.5 hover:bg-[rgba(31,78,121,0.06)]">
          <PanelLeft size={14} style={{ color: "var(--ink-3)" }} />
        </button>
      </div>
    );
  }

  const body = (
    <>
      <div className="flex items-center justify-between px-2.5 py-2 border-b" style={{ borderColor: "var(--hairline)" }}>
        <span className="font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>แผนผังไซต์</span>
        {narrow
          ? <button type="button" onClick={onCloseMobile} aria-label="ปิดแผนผังไซต์" data-testid="tree-close-mobile"><X size={14} /></button>
          : <button type="button" onClick={() => setCollapsed(true)} aria-label="ย่อแผนผังไซต์" data-testid="tree-collapse-toggle">
              <PanelLeftClose size={13} style={{ color: "var(--ink-3)" }} />
            </button>}
      </div>
      <div className="py-1">
        {noTenant && (
          <div data-testid="tree-no-tenant" className="px-2.5 py-2 font-mono"
               style={{ fontSize: 11, color: "var(--ink-3)", lineHeight: 1.5 }}>
            เลือกหน่วยงานที่แถบสถานะด้านล่าง
          </div>
        )}
        {!noTenant && plantsQ.isPending && <div className="px-2.5 py-2 font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>กำลังโหลด…</div>}
        {!noTenant && !plantsQ.isPending && plants.length === 0 && (
          <div data-testid="tree-no-sites" className="px-2.5 py-2 font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>ไม่มีไซต์</div>
        )}
        {plants.map((p) => (
          <PlantNode key={p.id} plant={p} expanded={expandedIds.includes(p.id)} onToggle={() => toggle(p.id)}
                      onNavigate={() => onCloseMobile?.()} />
        ))}
        {/* Now real — routes to /documents (DocumentsScreen, Stage B). */}
        <button type="button" data-testid="tree-packages-link"
                onClick={() => { navigate("/files/packages"); onCloseMobile?.(); }}
                className="w-full flex items-center gap-1.5 px-2.5 py-1.5 mt-1 border-t text-left hover:bg-[rgba(var(--accent-rgb)_/_0.08)]"
                style={{ fontSize: 11, color: "var(--ink-2)", borderColor: "var(--hairline)" }}>
          <Archive size={12} style={{ color: "var(--ink-4)" }} />
          แพ็กเกจเอกสาร
        </button>
      </div>
    </>
  );

  if (narrow) {
    return (
      <>
        <div className="fixed inset-0 z-[1190]" style={{ background: "rgba(10,15,24,0.35)" }} onClick={onCloseMobile} />
        <div className="fixed inset-y-0 left-0 z-[1200] w-[260px] max-w-[85vw] overflow-auto"
             style={{ background: "var(--paper-2)", boxShadow: "0 0 30px rgba(10,15,24,0.25)" }}>
          {body}
        </div>
      </>
    );
  }

  return (
    // 238px on --surface-2 (the handoff's sidebar), right border --hairline.
    <div className="w-[238px] shrink-0 border-r overflow-auto" style={{ background: "var(--surface-2)", borderColor: "var(--hairline)" }}>
      {body}
    </div>
  );
}
